import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTerraDraw, TerraDrawMode } from "src/hooks/useTerraDraw";
import type { GeoJSONStoreFeatures } from "terra-draw";
import PageLayout from "../../common/PageLayout";
import PageTitle from "../../common/PageTitle";
import VerticalDivider from "../../common/VerticalDivider";
import Button from "../../core/Button";
import { CreditsBalance } from "../common/CreditsBalance";
import { Search as SearchIcon, FilterIcon, HistoryIcon } from "lucide-react";
import { NeighborhoodSearchIcon } from "src/components/icons";
import ToggleButton from "../../core/ToggleButton";
import { toast } from "../../../components/shadcn/useToast";
import { LocationPermissionDialog } from "../components/LocationPermissionDialog";
import Layout, { useLayout } from "../../common/page/Layout";
import Content from "../../common/page/Content";
import Map, { MapTypeId, MapRef } from "../../core/Map";
import Marker from "../../core/map/Marker";
import PropertyMarker from "../components/PropertyMarker";
import { usePropertyFilters } from "./usePropertyFilters";
import { useCreateSavedSearchMutation } from "../hooks/useCreateSavedSearchMutation";
import { useInfiniteListSavedSearches } from "../hooks/useInfiniteListSavedSearches";
import { useSearchProperties } from "../hooks/useSearchProperties";
import {
  SearchPropertiesOptions,
  SavedSearch,
  SearchRegion,
} from "../../../types/neighborhoodSearch";
import { EnumProductTypes, Address } from "../../../types/listing";
import { GeoLocation } from "../../../types/common";
import {
  DEFAULT_PROPERTY_TYPES_FILTERS,
} from "../constants";
import SearchRegionShape from "../components/SearchRegionShape";
import { useDebounce } from "../../../components/hookHelpers";
import { DEFAULT_ZOOM, FALLBACK_MAP_CENTER } from "../../../constants/map";
import Sidebar, { SidebarTabs } from "./Sidebar";
import { SearchSidebarTabs } from "./SearchSidebar";
import { SavedSearchCardHeader } from "./ListSearchesSidebar/SavedSearchCardHeader";
import {
  useNeighborhoodSearchSettingsQuery,
  LocationType,
} from "src/hooks/useNeighborhoodSearchSettings";
import useCurrentUserGeoLocation from "src/hooks/useCurrentUserGeoLocation";
import { isAddressSavedSearch } from "../utils/isAddressSavedSearch";
import { isCombinedSavedSearch } from "../utils/isCombinedSavedSearch";
import { useIgnoreLocationPermission } from "../hooks/useIgnoreLocationPermission";
import useAccount from "src/hooks/useAccount";

const FALLBACK_MAP_CENTER_ZOOM = 5;
const PAGE_TITLE_HEIGHT = 50;

// Helper to extract coordinates from TerraDraw GeoJSON features
const getPolygonPointsFromGeoJSON = (coordinates: number[][]) => {
  return coordinates.map(([lng, lat]) => ({
    latitude: lat,
    longitude: lng,
  }));
};

const getBoundsFromPolygonCoordinates = (coordinates: number[][]) => {
  let north = -Infinity, south = Infinity, east = -Infinity, west = Infinity;
  for (const [lng, lat] of coordinates) {
    if (lat > north) north = lat;
    if (lat < south) south = lat;
    if (lng > east) east = lng;
    if (lng < west) west = lng;
  }
  return { north, south, east, west };
};

// TerraDraw styles matching our design
const TERRA_DRAW_STYLES = {
  strokeColor: "#38bdf8", // Tailwind sky-400
  fillColor: "#0284c7", // Tailwind sky-600
  fillOpacity: 0.35,
  strokeWidth: 2,
};

interface HandleSavedSearchClickOptions {
  suppressMapCenter?: boolean;
  suppressTabChange?: boolean;
}

const Search = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const { isSidebarOpen, toggleSidebar } = useLayout({
    isSidebarOpenInitial: tab === SidebarTabs.List,
  });
  const { data: settings, isLoading: isLoadingSettings } =
    useNeighborhoodSearchSettingsQuery();
  const { data: accountData } = useAccount();
  const mapComponentRef = useRef<MapRef | null>(null);

  const { refetch: getCurrentUserGeoLocation } = useCurrentUserGeoLocation();
  const { isIgnored: ignoreLocationPermission, clearIgnoreLocationPermission } = useIgnoreLocationPermission();
  const {
    selectedPropertyTypes,
    setSelectedPropertyTypes,
    beds,
    setBeds,
    baths,
    setBaths,
    sqft,
    setSqft,
    acreage,
    setAcreage,
    yearBuilt,
    setYearBuilt,
    setIsHideDNCPhoneNumbers,
    resetToDefaults: resetSearchFilters,
  } = usePropertyFilters({
    initialIsHideDNCPhoneNumbers: accountData?.is_hide_dnc_phone_numbers,
  });

  // Update property filters when settings load
  useEffect(() => {
    if (!isLoadingSettings && settings) {
      setSelectedPropertyTypes(
        settings.selectedPropertyTypes || DEFAULT_PROPERTY_TYPES_FILTERS
      );
      setBeds(settings.beds);
      setBaths(settings.baths);
      setSqft(settings.sqft);
      setAcreage(settings.acreage);
      setYearBuilt(settings.yearBuilt);

      // Also update searchCriteria with the loaded settings
      setSearchCriteria((prevCriteria) => ({
        ...prevCriteria,
        propertyType: settings.selectedPropertyTypes,
        bedrooms: settings.beds,
        bathrooms: settings.baths,
        squareFootage: settings.sqft,
        acreage: settings.acreage,
        yearBuilt: settings.yearBuilt,
      }));
    }
  }, [
    isLoadingSettings,
    settings,
    setSelectedPropertyTypes,
    setBeds,
    setBaths,
    setSqft,
    setAcreage,
    setYearBuilt,
  ]);

  // Update Hide DNC filter when account data loads
  useEffect(() => {
    if (accountData) {
      setIsHideDNCPhoneNumbers(accountData.is_hide_dnc_phone_numbers || false);
    }
  }, [accountData, setIsHideDNCPhoneNumbers]);

  const [addressSearchQuery, setAddressSearchQuery] = useState<string | null>(
    null
  );
  const [selectedSavedSearches, setSelectedSavedSearches] = useState<
    SavedSearch[]
  >([]);
  const [activeNewSearchTab, setActiveNewSearchTab] =
    useState<SearchSidebarTabs | undefined>(SearchSidebarTabs.RegionSearchResults);
  const [newRegionSearchName, setNewRegionSearchName] = useState<string>("");
  const [newAddressSearchName, setNewAddressSearchName] = useState<string>("");
  const [mapExpandedSavedSearch, setMapExpandedSavedSearch] =
    useState<SavedSearch | null>(null);
  const [triggerSearchToolbarPulse, setTriggerSearchToolbarPulse] = useState<boolean>(false);
  const [expandedSavedSearch, setExpandedSavedSearch] =
    useState<SavedSearch | null>(null);
  const [focusedSearchId, setFocusedSearchId] = useState<string | null>(null);
  const [hoveredSavedSearch, setHoveredSavedSearch] =
    useState<SavedSearch | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [mapCenter, setMapCenter] = useState<GeoLocation | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [zoom, setZoom] = useState<number>(FALLBACK_MAP_CENTER_ZOOM);
  const [drawingMode, setDrawingMode] = useState<TerraDrawMode>(null);
  const [isSavedSearchesVisible, setIsSavedSearchesVisible] =
    useState<boolean>(false);
  const [searchRegion, setSearchRegion] = useState<SearchRegion | undefined>(
    undefined
  );
  const [searchCriteria, setSearchCriteria] =
    useState<SearchPropertiesOptions | null>({
      searchType: "none",
      propertyType: selectedPropertyTypes,
      bedrooms: beds,
      bathrooms: baths,
      squareFootage: sqft,
      acreage: acreage,
      yearBuilt: yearBuilt,
    });
  const debouncedSearchCriteria = useDebounce(searchCriteria, 300);

  const { mutateAsync: createSavedSearch, isPending: isSavingSearch } =
    useCreateSavedSearchMutation();
  const {
    data: searchPropertiesResult,
    isFetching: isFetchingProperties,
    error: errorFetchingProperties,
    isError: isErrorFetchingProperties,
  } = useSearchProperties(debouncedSearchCriteria);

  const [listSearchesSearchTerm, setListSearchesSearchTerm] =
    useState<string>("");
  const debouncedListSearchesSearchTerm = useDebounce(
    listSearchesSearchTerm,
    300
  );

  const {
    data: savedSearchesPages,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingSavedSearches,
  } = useInfiniteListSavedSearches({
    search: debouncedListSearchesSearchTerm,
  });

  const savedSearches = useMemo(
    () => savedSearchesPages?.pages.flat() || [],
    [savedSearchesPages]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (isFetchingProperties) {
      // Pop the sidebar when we're fetching properties
      toggleSidebar(true);
      setActiveSidebarTab(SidebarTabs.Search);
      setActiveNewSearchTab(
        searchCriteria?.searchType === "address"
          ? SearchSidebarTabs.AddressSearchResults
          : SearchSidebarTabs.RegionSearchResults
      );
    }
  }, [isFetchingProperties, toggleSidebar, searchCriteria?.searchType]);

  const handleSavedSearchClick = useCallback(
    (
      newSelected: SavedSearch | undefined | null,
      options: HandleSavedSearchClickOptions = {}
    ) => {
      const { suppressMapCenter = false, suppressTabChange = false } = options;

      if (!newSelected) {
        return;
      }

      setFocusedSearchId(newSelected.id);

      if (!suppressMapCenter) {
        // Handle address-based saved searches
        if (isAddressSavedSearch(newSelected)) {
          const addressLocation =
            newSelected.search_parameters?.searchAddress?.location;
          if (addressLocation) {
            setMapCenter({
              lat: addressLocation.lat,
              lng: addressLocation.lng,
            });
            setZoom(DEFAULT_ZOOM);
          }
        } else if (isCombinedSavedSearch(newSelected)) {
          // Pick the search region from the first search source
          const firstSearchRegion =
            newSelected.search_parameters?.combinedSearchSources?.[0]
              ?.searchRegion;
          if (firstSearchRegion) {
            const mapCenter = firstSearchRegion.mapCenter;
            if (mapCenter) {
              setMapCenter({
                lat: mapCenter.lat,
                lng: mapCenter.lng,
              });
            }
            setZoom(firstSearchRegion.zoom || DEFAULT_ZOOM);
          }
        } else {
          // Handle region-based saved searches
          const searchRegion = newSelected.search_parameters?.searchRegion;
          if (searchRegion) {
            setMapCenter({
              lat: searchRegion.mapCenter.lat,
              lng: searchRegion.mapCenter.lng,
            });
            setZoom(searchRegion.zoom || DEFAULT_ZOOM);
          }
        }
      }
      setIsSavedSearchesVisible(true);
      if (!suppressTabChange) {
        setActiveSidebarTab(SidebarTabs.List);
      }
    },
    []
  );

  const [hasInitialized, setHasInitialized] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [pendingMapCenter, setPendingMapCenter] = useState<GeoLocation | null>(null);
  const [pendingZoom, setPendingZoom] = useState<number | null>(null);

  // Handle map load event
  const handleMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
    setIsMapLoaded(true);
    // If we have a pending center from settings, apply it now
    if (pendingMapCenter && mapComponentRef.current) {
      mapComponentRef.current.panTo(pendingMapCenter);
      if (pendingZoom !== null) {
        mapComponentRef.current.setZoom(pendingZoom);
      }
      setPendingMapCenter(null);
      setPendingZoom(null);
    }
  }, [pendingMapCenter, pendingZoom]);

  useEffect(() => {
    const initializeMapCenter = async () => {
      if (
        !isLoadingSavedSearches &&
        savedSearches &&
        settings &&
        !hasInitialized
      ) {
        setHasInitialized(true);

        let targetCenter: GeoLocation | null = null;
        let targetZoom = DEFAULT_ZOOM;

        if (
          settings.defaultAddressGeoLocation &&
          settings.defaultLocationType === LocationType.address
        ) {
          // If the default location type is address, use the default geo location
          targetCenter = settings.defaultAddressGeoLocation;
          targetZoom = DEFAULT_ZOOM;
        } else if ((settings.defaultLocationType === LocationType.myLocation || !settings.defaultLocationType) && !ignoreLocationPermission) {
          // If the default location type is my location and user hasn't ignored location permission,
          // try to get current user geo location
          const { data: currentLocation, error } =
            await getCurrentUserGeoLocation();
          if (currentLocation) {
            // Successfully got user's location
            targetCenter = currentLocation;
            targetZoom = DEFAULT_ZOOM;
            // Clear the ignore flag since user has now granted location permission
            clearIgnoreLocationPermission();
          } else if (error) {
            console.error("Error getting user location:", error);
            // Use fallback location
            targetCenter = FALLBACK_MAP_CENTER;
            targetZoom = FALLBACK_MAP_CENTER_ZOOM;

            // Only show dialog if user hasn't already chosen to ignore location permission
            if (!ignoreLocationPermission) {
              setShowLocationDialog(true);
            }
          }
        } else {
          // Use fallback for other cases
          targetCenter = FALLBACK_MAP_CENTER;
          targetZoom = FALLBACK_MAP_CENTER_ZOOM;
        }

        // Apply the center and zoom
        if (targetCenter) {
          setMapCenter(targetCenter);
          setZoom(targetZoom);

          // If map is already loaded, pan to the new center immediately
          if (isMapLoaded && mapComponentRef.current) {
            mapComponentRef.current.panTo(targetCenter);
            mapComponentRef.current.setZoom(targetZoom);
          } else {
            // Otherwise, store it to apply when map loads
            setPendingMapCenter(targetCenter);
            setPendingZoom(targetZoom);
          }
        }
      }
    };

    initializeMapCenter();
  }, [
    settings,
    savedSearches,
    getCurrentUserGeoLocation,
    isLoadingSavedSearches,
    hasInitialized,
    handleSavedSearchClick,
    ignoreLocationPermission,
    clearIgnoreLocationPermission,
    isMapLoaded,
  ]);

  const goToSavedSearch = useCallback(
    (searchId?: string) => {
      const selectedIds = searchId
        ? searchId
        : selectedSavedSearches.map((search) => search.id).join(",");
      navigate(`/ns/searches/list?selected=${selectedIds}`);
    },
    [selectedSavedSearches, navigate]
  );

  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTabs>(
    tab === SidebarTabs.List
      ? SidebarTabs.List
      : SidebarTabs.Search
  );

  const sidebarTitle = useMemo(() => {
    if (activeSidebarTab === SidebarTabs.Search) {
      return "Filters";
    }
    if (activeSidebarTab === SidebarTabs.List) {
      return "Saved Searches";
    }
    return "";
  }, [activeSidebarTab]);

  // Handle filters button click
  const handleFiltersClick = useCallback(() => {
    if (isSidebarOpen && activeSidebarTab === SidebarTabs.Search) {
      // Hide sidebar if it's open and showing the filters (Search tab)
      toggleSidebar(false);
    } else {
      // Show sidebar with filters
      toggleSidebar(true);
      setActiveSidebarTab(SidebarTabs.Search);
    }
  }, [toggleSidebar, isSidebarOpen, activeSidebarTab]);

  // Handle saved searches button click
  const handleSavedSearchesClick = useCallback(() => {
    if (isSidebarOpen && activeSidebarTab === SidebarTabs.List) {
      // Hide sidebar if it's open and showing saved searches (List tab)
      toggleSidebar(false);
    } else {
      // Show sidebar with saved searches
      toggleSidebar(true);
      setActiveSidebarTab(SidebarTabs.List);
      navigate("/ns/search/list");
    }
  }, [toggleSidebar, isSidebarOpen, activeSidebarTab, navigate]);


  // Update active tab when URL parameter changes
  useEffect(() => {
    const newTab =
      tab === SidebarTabs.List
        ? SidebarTabs.List
        : SidebarTabs.Search;
    setActiveSidebarTab(newTab);
  }, [tab]);

  // Pulse toolbar when starting in search mode
  useEffect(() => {
    if (tab !== SidebarTabs.List) {
      setTriggerSearchToolbarPulse(prev => !prev);
    }
  }, [tab]);

  const handleSaveSearchClick = useCallback(
    async ({ searchName }: { searchName: string }) => {
      try {
        const { id: newSearchId } = await createSavedSearch({
          listName: searchName,
          listOptions: debouncedSearchCriteria,
          searchRegion,
        });

        if (newSearchId) {
          goToSavedSearch(newSearchId);
        }
      } catch (error) {
        toast({
          title: "Error saving search",
          description: "Please try again",
        });
        console.error("Error saving search:", error);
      }
    },
    [debouncedSearchCriteria, createSavedSearch, searchRegion, goToSavedSearch]
  );

  const updateSearchCriteria = useCallback(
    (newSearchCriteria: SearchPropertiesOptions) => {
      setSearchCriteria((prevCriteria) => {
        if (!prevCriteria) {
          return null;
        }

        return {
          ...prevCriteria,
          ...(newSearchCriteria?.geo && { address: undefined }),
          ...(newSearchCriteria?.address && {
            geo: undefined,
          }),
          ...newSearchCriteria,
        };
      });
    },
    []
  );

  const handlePropertyTypesChange = (selectedTypes: string[]) => {
    setSelectedPropertyTypes(selectedTypes);
    updateSearchCriteria({ propertyType: selectedTypes });
  };

  const handleBedsChange = (newBeds: (number | undefined)[]) => {
    setBeds(newBeds);
    updateSearchCriteria({
      bedrooms: newBeds,
    });
  };

  const handleBathsChange = (newBaths: (number | undefined)[]) => {
    setBaths(newBaths);
    updateSearchCriteria({
      bathrooms: newBaths,
    });
  };

  const handleSqftChange = (newSqft: (number | undefined)[]) => {
    setSqft(newSqft);
    updateSearchCriteria({
      squareFootage: newSqft,
    });
  };

  const handleAcreageChange = (newAcreage: (number | undefined)[]) => {
    setAcreage(newAcreage);
    updateSearchCriteria({
      acreage: newAcreage,
    });
  };

  const handleYearBuiltChange = (newYearBuilt: (number | undefined)[]) => {
    setYearBuilt(newYearBuilt);
    updateSearchCriteria({
      yearBuilt: newYearBuilt,
    });
  };

  const handleAddressSelected = useCallback(
    ({
      address,
      formattedAddress,
      location,
    }: {
      address: Address;
      formattedAddress: string;
      location: GeoLocation;
    }) => {
      if (address) {
        setAddressSearchQuery(formattedAddress);
        setNewAddressSearchName(formattedAddress);
        updateSearchCriteria({
          searchType: "address",
          address,
          ...(location && {
            addressLocation: location,
          }),
        });
      }
    },
    [updateSearchCriteria]
  );

  // Handle TerraDraw shape changes
  const handleTerraDrawShapeChange = useCallback((features: GeoJSONStoreFeatures[]) => {
    // Find the most recent completed feature (last in array that has valid geometry)
    // Filter to only features with mode property (completed shapes, not selection handles)
    const completedFeatures = features.filter(f => f.properties?.mode && f.geometry);
    const feature = completedFeatures.length > 0 ? completedFeatures[completedFeatures.length - 1] : null;

    if (!feature || !feature.geometry) {
      // No completed features - but don't clear search if we have an active search
      // (this can happen during mode transitions)
      return;
    }

    const geometry = feature.geometry;
    const featureType = feature.properties?.mode as string;

    if (featureType === "circle" && geometry.type === "Polygon") {
      // TerraDraw stores circles as polygons with radiusKilometers in properties
      // Center is not exposed in properties, so we calculate it from the polygon centroid
      const radiusKm = feature.properties?.radiusKilometers as number | undefined;
      const coords = geometry.coordinates?.[0] as number[][] | undefined;

      if (coords && coords.length > 1 && radiusKm !== undefined && radiusKm > 0) {
        // Exclude last coordinate (duplicate of first to close polygon)
        const uniqueCoords = coords.slice(0, -1);
        const lng = uniqueCoords.reduce((sum, coord) => sum + coord[0], 0) / uniqueCoords.length;
        const lat = uniqueCoords.reduce((sum, coord) => sum + coord[1], 0) / uniqueCoords.length;
        const radiusMeters = radiusKm * 1000;

        setSearchRegion({
          type: "circle",
          circle: {
            circleCenter: { lat, lng },
            circleRadius: radiusMeters,
          },
          zoom,
          mapCenter: { lat, lng },
          fullScreenMode: false,
        });

        updateSearchCriteria({
          searchType: "circle",
          geo: {
            circle: {
              center: { latitude: lat, longitude: lng },
              radius: radiusMeters,
            },
          },
        });
      }
    } else if (featureType === "rectangle" && geometry.type === "Polygon") {
      // Rectangle is stored as a polygon - extract bounds
      const coordinates = geometry.coordinates[0] as number[][];
      const bounds = getBoundsFromPolygonCoordinates(coordinates);

      setSearchRegion({
        type: "rectangle",
        rectangleBounds: bounds,
        zoom,
        mapCenter: {
          lat: (bounds.north + bounds.south) / 2,
          lng: (bounds.west + bounds.east) / 2,
        },
        fullScreenMode: false,
      });

      updateSearchCriteria({
        searchType: "polygon",
        geo: {
          polygon: {
            points: [
              { latitude: bounds.north, longitude: bounds.west },
              { latitude: bounds.north, longitude: bounds.east },
              { latitude: bounds.south, longitude: bounds.east },
              { latitude: bounds.south, longitude: bounds.west },
              { latitude: bounds.north, longitude: bounds.west },
            ],
          },
        },
      });
    } else if (featureType === "polygon" && geometry.type === "Polygon") {
      const coordinates = geometry.coordinates[0] as number[][];
      const polygonVertices = getPolygonPointsFromGeoJSON(coordinates);

      setSearchRegion({
        type: "polygon",
        polygonVertices: polygonVertices.map((v) => ({ lat: v.latitude, lng: v.longitude })),
        zoom,
        mapCenter: { lat: 0, lng: 0 },
        fullScreenMode: false,
      });

      updateSearchCriteria({
        searchType: "polygon",
        geo: {
          polygon: {
            points: polygonVertices,
          },
        },
      });
    }
  }, [updateSearchCriteria, zoom]);

  // Handle drawing mode change from TerraDraw (auto-switch to select after drawing)
  const handleTerraDrawModeChange = useCallback((mode: TerraDrawMode) => {
    setDrawingMode(mode);
  }, []);

  // TerraDraw hook for shape drawing and editing
  const { clear: clearTerraDraw } = useTerraDraw({
    map: mapInstance,
    mode: drawingMode,
    styles: TERRA_DRAW_STYLES,
    onShapeChange: handleTerraDrawShapeChange,
    onModeChange: handleTerraDrawModeChange,
  });

  // Handle drawing mode change from toolbar - clear existing shapes when starting a new drawing
  const handleToolbarDrawingModeChange = useCallback((mode: TerraDrawMode) => {
    // If switching to a drawing mode (not select or null), clear existing shapes and geo filter
    if (mode && mode !== "select") {
      clearTerraDraw();
      setSearchRegion(undefined);
      setSearchCriteria(prev => prev ? {
        ...prev,
        searchType: "none",
        geo: undefined,
      } : null);
    }
    setDrawingMode(mode);
  }, [clearTerraDraw]);

  // Clear selections using TerraDraw
  const clearSelections = useCallback(() => {
    clearTerraDraw();
  }, [clearTerraDraw]);

  const handleNewSearchClick = useCallback(() => {
    clearSelections();
    resetSearchFilters();
    setSearchRegion(undefined);
    setSearchCriteria({
      searchType: "none",
      propertyType: selectedPropertyTypes,
      bedrooms: beds,
      bathrooms: baths,
      squareFootage: sqft,
      acreage: acreage,
      yearBuilt: yearBuilt,
    });
    setNewRegionSearchName("");
    setNewAddressSearchName("");
    setActiveNewSearchTab(SearchSidebarTabs.RegionSearchResults);
    toggleSidebar(false);
    setTriggerSearchToolbarPulse(prev => !prev);
    navigate("/ns/search/start");
  }, [
    resetSearchFilters,
    setNewRegionSearchName,
    clearSelections,
    navigate,
    toggleSidebar,
    selectedPropertyTypes,
    beds,
    baths,
    sqft,
    acreage,
    yearBuilt,
  ]);

  const handleClearSearchClick = useCallback(() => {
    clearSelections();
    resetSearchFilters();
    setSearchRegion(undefined);
    setSearchCriteria({
      searchType: "none",
      propertyType: selectedPropertyTypes,
      bedrooms: beds,
      bathrooms: baths,
      squareFootage: sqft,
      acreage: acreage,
      yearBuilt: yearBuilt,
    });
    setNewRegionSearchName("");
    setNewAddressSearchName("");
    setActiveNewSearchTab(SearchSidebarTabs.RegionSearchResults);
  }, [
    resetSearchFilters,
    setNewRegionSearchName,
    clearSelections,
    selectedPropertyTypes,
    beds,
    baths,
    sqft,
    acreage,
    yearBuilt,
    setActiveNewSearchTab,
    setNewAddressSearchName,
  ]);

  const handleSavedSearchesSelectionChange = useCallback(
    (search: SavedSearch, isSelected: boolean) => {
      setSelectedSavedSearches((prev) => {
        if (isSelected) {
          return [...prev, search];
        } else {
          return prev.filter((s) => s.id !== search.id);
        }
      });
    },
    []
  );

  const handleSearchExpandToggleClick = useCallback((search: SavedSearch) => {
    setExpandedSavedSearch((current) => {
      return current?.id === search.id ? null : search;
    });
  }, []);

  return (
    <PageLayout
      suppressGutters
      suppressTopPadding
      pinnedHeaderHeight={PAGE_TITLE_HEIGHT}
      pinnedHeader={
        <PageTitle
          title="Neighborhood Search"
          titleIcon={<NeighborhoodSearchIcon />}
          isCondensed
          isUseGutters
          rightSideContent={
            <div className="flex flex-row items-center gap-2">
              <CreditsBalance />
              <VerticalDivider className="h-full" />
              <ToggleButton
                label="Show Saved Searches"
                isEnabled={isSavedSearchesVisible}
                onChange={setIsSavedSearchesVisible}
              />
              <Button
                variant="outline"
                onClick={handleNewSearchClick}
                icon={<SearchIcon />}
              >
                New Search
              </Button>
              <Button
                variant={isSidebarOpen && activeSidebarTab === SidebarTabs.List ? "default" : "outline"}
                onClick={handleSavedSearchesClick}
                icon={<HistoryIcon />}
              >
                Saved Searches
              </Button>
              <Button
                variant={isSidebarOpen && activeSidebarTab === SidebarTabs.Search ? "default" : "secondary"}
                icon={<FilterIcon />}
                onClick={handleFiltersClick}
              >
                Filters
              </Button>
            </div>
          }
        />
      }
    >
      <Layout
        isSidebarOpen={isSidebarOpen}
        onSidebarToggleClick={toggleSidebar}
        SidebarProps={{
          size: "sm",
          offsetHeight: 50,
          title: sidebarTitle,
        }}
        sidebarContent={
          <Sidebar
            activeTab={activeSidebarTab}
            activeNewSearchTab={activeNewSearchTab}
            onLoadSearchesClick={() => goToSavedSearch()}
            hasSearchResults={!!searchPropertiesResult || isFetchingProperties}
            RegionSearchResultsProps={{
              searchName: newRegionSearchName,
              onSearchNameChange: setNewRegionSearchName,
              onSaveSearchClick: handleSaveSearchClick,
              onClearSearchClick: handleClearSearchClick,
              numProperties: searchPropertiesResult?.count || 0,
              isFetching: isFetchingProperties,
              isSaving: isSavingSearch,
              error: errorFetchingProperties,
              isError: isErrorFetchingProperties,
            }}
            AddressSearchResultsProps={{
              searchName: newAddressSearchName,
              onSearchNameChange: setNewAddressSearchName,
              onSaveSearchClick: handleSaveSearchClick,
              onClearSearchClick: handleClearSearchClick,
              searchQueryAddress: addressSearchQuery,
              searchResultAddress: searchPropertiesResult?.properties?.[0],
              creditCost: searchPropertiesResult?.count ?? 0,
              isFetching: isFetchingProperties,
              isSaving: isSavingSearch,
              error: errorFetchingProperties,
              isError: isErrorFetchingProperties,
            }}
            ListSearchesSidebarProps={{
              searches: savedSearches,
              isLoading: isLoadingSavedSearches,
              searchTerm: listSearchesSearchTerm,
              onSearchTermChange: setListSearchesSearchTerm,
              expandedSearchId: expandedSavedSearch?.id,
              mapExpandedSavedSearchId: mapExpandedSavedSearch?.id,
              focusedSearchId: focusedSearchId,
              onSearchClick: handleSavedSearchClick,
              onSearchSelectionChange: handleSavedSearchesSelectionChange,
              selectedSearches: selectedSavedSearches,
              onScrollBottomReached: handleLoadMore,
              onSearchToggleClick: handleSearchExpandToggleClick,
            }}
            PropertyFiltersProps={{
              selectedPropertyTypes,
              onSelectedPropertyTypesChange: handlePropertyTypesChange,
              beds,
              onBedsChange: handleBedsChange,
              baths,
              onBathsChange: handleBathsChange,
              sqft,
              onSqftChange: handleSqftChange,
              acreage,
              onAcreageChange: handleAcreageChange,
              yearBuilt,
              onYearBuiltChange: handleYearBuiltChange,
              disabled: isFetchingProperties,
            }}
          />
        }
      >
        <Content>
          <div className="map-container relative w-full" style={{ height: `calc(100vh - ${PAGE_TITLE_HEIGHT}px)` }}>
            <Map
              ref={mapComponentRef}
              mapCenter={mapCenter || FALLBACK_MAP_CENTER}
              zoom={zoom}
              onZoomChange={setZoom}
              onMapLoad={handleMapLoad}
              initialMapTypeId={settings?.defaultMapTypeId || MapTypeId.Roadmap}
              onAddressSelected={handleAddressSelected}
              suppressAddressMarker={!!searchPropertiesResult?.properties?.length}
              drawingMode={drawingMode}
              onDrawingModeChange={(mode) => handleToolbarDrawingModeChange(mode as TerraDrawMode)}
              triggerSearchToolbarPulse={triggerSearchToolbarPulse}
              enableSelectionToolbar={true}
            >
                            {isSavedSearchesVisible &&
                savedSearches.map((search) => {
                  const region = search.search_parameters?.searchRegion;
                  return region ? (
                    <SearchRegionShape key={search.id} {...region} />
                  ) : null;
                })}
              {isSavedSearchesVisible &&
                savedSearches.map((savedSearch) => {
                  const region = savedSearch.search_parameters?.searchRegion;
                  return region ? (
                    <SearchRegionShape
                      key={savedSearch.id}
                      onClick={() => {
                        handleSavedSearchClick(savedSearch, {
                          suppressMapCenter: true,
                        });
                        setMapExpandedSavedSearch(savedSearch);
                      }}
                      {...region}
                      isSelected={expandedSavedSearch?.id === savedSearch.id}
                    />
                  ) : null;
                })}
              {isSavedSearchesVisible &&
                savedSearches.map((savedSearch) => {
                  const {
                    id: searchId,
                    name = "",
                    result_count: numProperties = 0,
                    create_timestamp: createdAt = "",
                    search_parameters: searchParameters,
                  } = savedSearch;
                  let geoLocation: GeoLocation | undefined;
                  if (isAddressSavedSearch(savedSearch)) {
                    geoLocation = searchParameters?.searchAddress?.location;
                  } else {
                    geoLocation = searchParameters?.searchRegion?.mapCenter;
                  }
                  if (geoLocation) {
                    return (
                      <Marker
                        iconSize={40}
                        key={`saved-search-marker-${searchId}`}
                        iconUrl={
                          focusedSearchId === savedSearch.id
                            ? `${process.env.PUBLIC_URL}/neighborhoodSearch/markers/saved-search-selected.svg`
                            : `${process.env.PUBLIC_URL}/neighborhoodSearch/markers/saved-search.svg`
                        }
                        position={geoLocation}
                        isHovered={hoveredSavedSearch === savedSearch}
                        onMouseOver={() => {
                          setHoveredSavedSearch(savedSearch);
                        }}
                        onMouseOut={() => {
                          setHoveredSavedSearch(null);
                        }}
                        onClick={() => {
                          handleSavedSearchClick(savedSearch);
                          setMapExpandedSavedSearch(savedSearch);
                        }}
                      >
                        <div className="min-w-[250px]">
                          <SavedSearchCardHeader
                            name={name}
                            numProperties={numProperties}
                            createdAt={createdAt}
                          />
                        </div>
                      </Marker>
                    );
                  }
                  return null;
                })}
              {searchPropertiesResult?.properties?.map((property, index) => {
                const geoLocation = property?.geolocation;

                if (geoLocation) {
                  return (
                    <PropertyMarker
                      key={`property-marker-${index}`}
                      productType={EnumProductTypes.ns}
                      extension="green"
                      position={{
                        lat: geoLocation.latitude,
                        lng: geoLocation.longitude,
                      }}
                    />
                  );
                }
                return null;
              })}
            </Map>
          </div>
        </Content>
      </Layout>
      
      <LocationPermissionDialog
        isOpen={showLocationDialog}
        onClose={() => setShowLocationDialog(false)}
      />
    </PageLayout>
  );
};
export default Search;
